import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Slider,
  InputAdornment,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { motion } from 'framer-motion';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import TuneIcon from '@mui/icons-material/Tune';
import { API_ENDPOINTS } from '../config/api';

const AlertConfigTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [provider, setProvider] = useState('groq');
  const [defaultProvider, setDefaultProvider] = useState('groq');
  const [guestProvider, setGuestProvider] = useState('groq');
  const [providerOptions, setProviderOptions] = useState(['groq', 'openrouter']);
  const [providerMetadata, setProviderMetadata] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [savingProvider, setSavingProvider] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [updatingCompanyId, setUpdatingCompanyId] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const handleForbidden = (text) => {
    setAccessDenied(true);
    setMessage({ type: 'error', text: text || 'Acesso restrito. Apenas administradores podem alterar as configurações.' });
  };

  useEffect(() => {
    if (accessDenied) {
      return;
    }
    loadConfig();
    loadProviderData();
    fetchProviderStatus();
    loadCompanyProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessDenied]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.CONFIG, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) {
        handleForbidden('Aba de configurações disponível apenas para administradores.');
        setLoading(false);
        return;
      }
      const data = await response.json();
      if (response.ok && data.success) {
        setConfig(data.data);
      } else {
        setMessage({ type: 'error', text: data.message || 'Erro ao carregar configurações' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao conectar com servidor' });
    } finally {
      setLoading(false);
    }
  };

  const loadProviderData = async () => {
    try {
      setLoadingProviders(true);
      const response = await fetch(API_ENDPOINTS.INSIGHTS_CONFIG, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) {
        handleForbidden('Acesso restrito ao painel de insights.');
        setLoadingProviders(false);
        return;
      }
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Erro ao carregar dados de provedor');
      }
      const info = data.data || {};
      setDefaultProvider(info.defaultProvider || 'groq');
      setProvider(info.defaultProvider || 'groq');
      setGuestProvider(info.guestProvider || 'groq');
      if (Array.isArray(info.providers)) {
        setProviderMetadata(info.providers);
      }
      if (Array.isArray(info.availableProviders) && info.availableProviders.length > 0) {
        setProviderOptions(info.availableProviders);
      } else if (Array.isArray(info.providers)) {
        setProviderOptions(info.providers.map((p) => p.key));
      }
    } catch (error) {
      setMessage((prev) => prev || { type: 'error', text: error.message || 'Erro ao carregar provedor de LLM' });
    } finally {
      setLoadingProviders(false);
    }
  };

  const fetchProviderStatus = async () => {
    if (accessDenied) return;
    try {
      const response = await fetch(API_ENDPOINTS.INSIGHTS_STATUS, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) {
        handleForbidden('Acesso restrito ao status dos provedores.');
        return;
      }
      const data = await response.json();
      if (response.ok && data.success) {
        setStatusMap(data.data?.providers || {});
        if (data.data?.guestProvider) {
          setGuestProvider(data.data.guestProvider);
        }
      } else {
        setStatusMap({});
        if (data.message) {
          setMessage((prev) => prev || { type: 'error', text: data.message });
        }
      }
    } catch (error) {
      setStatusMap({});
      setMessage((prev) => prev || { type: 'error', text: error.message || 'Erro ao consultar status dos provedores' });
    }
  };

  const loadCompanyProviders = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.INSIGHTS_COMPANY_PROVIDERS, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) {
        handleForbidden('Somente administradores podem gerenciar provedores por empresa.');
        return;
      }
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Erro ao carregar empresas');
      }
      const payload = data.data || {};
      if (Array.isArray(payload.companies)) {
        setCompanies(payload.companies);
      } else {
        setCompanies([]);
      }
      if (payload.defaultProvider) {
        setDefaultProvider(payload.defaultProvider);
        setProvider(payload.defaultProvider);
      }
      if (payload.guestProvider) {
        setGuestProvider(payload.guestProvider);
      }
      if (Array.isArray(payload.availableProviders) && payload.availableProviders.length > 0) {
        setProviderOptions(payload.availableProviders);
      }
    } catch (error) {
      setCompanies([]);
      setMessage((prev) => prev || { type: 'error', text: error.message || 'Erro ao carregar empresas' });
    }
  };

  const handleSave = async () => {
    if (accessDenied) return;
    try {
      setSaving(true);
      setMessage(null);

      const response = await fetch(API_ENDPOINTS.CONFIG, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(config),
      });

      if (response.status === 401 || response.status === 403) {
        handleForbidden('Apenas administradores podem salvar as configurações.');
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
        setConfig(data.data);
      } else {
        setMessage({ type: 'error', text: data.message || 'Erro ao salvar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
    } finally {
      setSaving(false);
    }
  };

  // A caixa cinza do sistema operacional que ficava aqui não diz o que será
  // desfeito, não combina com a tela e pode ser suprimida pelo navegador — o que
  // transformaria a ação destrutiva numa ação sem confirmação nenhuma.
  const [confirmarRestauracao, setConfirmarRestauracao] = useState(false);

  const handleReset = async () => {
    if (accessDenied) return;
    setConfirmarRestauracao(false);

    try {
      setSaving(true);
      setMessage(null);

      const response = await fetch(API_ENDPOINTS.CONFIG_RESET, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.status === 401 || response.status === 403) {
        handleForbidden('Somente administradores podem restaurar as configurações.');
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Configurações restauradas para o padrão!' });
        setConfig(data.data);
      } else {
        setMessage({ type: 'error', text: data.message || 'Erro ao restaurar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao restaurar configurações' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (indicator, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [indicator]: {
        ...prev[indicator],
        [field]: parseFloat(value) || 0,
      },
    }));
  };

  const handleProviderSave = async () => {
    if (accessDenied) return;
    try {
      setSavingProvider(true);
      setMessage(null);
      const response = await fetch(API_ENDPOINTS.INSIGHTS_CONFIG, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider }),
      });
      if (response.status === 401 || response.status === 403) {
        handleForbidden('Somente administradores podem alterar o provedor padr�o.');
        return;
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erro ao atualizar provedor');
      }
      setMessage({ type: 'success', text: 'Provedor de LLM atualizado!' });
      await loadProviderData();
      await fetchProviderStatus();
      await loadCompanyProviders();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Erro ao atualizar provedor' });
    } finally {
      setSavingProvider(false);
    }
  };

  const handleCompanyProviderChange = async (companyId, providerValue) => {
    if (accessDenied) return;
    try {
      setUpdatingCompanyId(companyId);
      setMessage(null);
      const response = await fetch(`${API_ENDPOINTS.INSIGHTS_COMPANY_PROVIDERS}/${companyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider: providerValue }),
      });
      if (response.status === 401 || response.status === 403) {
        handleForbidden('Somente administradores podem alterar provedores por empresa.');
        return;
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Erro ao atualizar provedor da empresa');
      }
      const updated = data.data?.company;
      if (updated) {
        setCompanies((prev) =>
          prev.map((company) =>
            company.id === updated.id
              ? {
                  ...company,
                  provider: updated.provider,
                  updatedAt: updated.updatedAt || updated.llm_provider_updated_at,
                }
              : company
          )
        );
      }
      setMessage({ type: 'success', text: 'Provedor da empresa atualizado!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Erro ao atualizar provedor da empresa' });
    } finally {
      setUpdatingCompanyId(null);
    }
  };

  const providerLabelMap = providerMetadata.reduce((acc, meta) => {
    if (!meta || !meta.key) {
      return acc;
    }
    const name = meta.key === 'openrouter' ? 'OpenRouter' : 'Groq';
    acc[meta.key] = `${name} (${meta.model || 'modelo não definido'})`;
    return acc;
  }, {});
  const getProviderLabel = (key) => providerLabelMap[key] || (key === 'openrouter' ? 'OpenRouter' : 'Groq');
  const currentStatus = statusMap[provider];
  const currentStatusLabel = currentStatus
    ? currentStatus.ok
      ? 'Status: operacional'
      : `Status: erro${currentStatus.message ? ` (${currentStatus.message})` : ''}`
    : 'Status: desconhecido';
  const currentStatusColor = currentStatus?.ok ? 'success' : 'error';
  const defaultProviderLabel = getProviderLabel(defaultProvider);
  const guestProviderLabel = getProviderLabel(guestProvider);
  const currentProviderLabel = getProviderLabel(provider);
  const liquidezConfig = config?.liquidez ?? { label: 'Liquidez Corrente', description: '', critico: 0, atencao: 0, positivo: 0 };
  const margemConfig = config?.margemLiquida ?? { label: 'Margem Líquida', description: '', critico: 0, atencao: 0, positivo: 0 };
  const endividamentoConfig = config?.endividamento ?? { label: 'Endividamento', description: '', critico: 0, atencao: 0 };
  const crescimentoConfig = config?.crescimento ?? { label: 'Crescimento', description: '', critico: 0, atencao: 0, positivo: 0 };
  const formatDateTime = (value) => {
    if (!value) return 'Nunca atualizado';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch (error) {
      return value;
    }
  };

  if (accessDenied) {
    return (
      <Box sx={{ p: 4 }}>
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}
        <Alert severity="warning">
          Acesso restrito. Contate o administrador para alterar as configurações de alertas e provedores.
        </Alert>
      </Box>
    );
  }

  if (loading || !config) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Carregando configurações...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TuneIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Configurações de Alertas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Personalize os limites dos indicadores financeiros
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Messages */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: 24 }}
        >
          <Alert severity={message.type} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        </motion.div>
      )}

      {/* Configurações */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">Provedor LLM</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Escolha qual provedor será usado para gerar os insights consultivos (somente administradores).
                  </Typography>
                  <RadioGroup
                    row
                    value={provider}
                    onChange={(event) => setProvider(event.target.value)}
                    sx={{ mt: 2 }}
                  >
                    {providerOptions.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio />}
                        label={getProviderLabel(option)}
                      />
                    ))}
                  </RadioGroup>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleProviderSave}
                      disabled={savingProvider}
                      startIcon={savingProvider ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {savingProvider ? 'Salvando...' : 'Salvar provedor'}
                    </Button>
                    <Chip label={`Atual: ${currentProviderLabel}`} color="primary" size="small" />
                    <Chip label={currentStatusLabel} color={currentStatusColor} size="small" />
                    <Chip label={`Convidados: ${guestProviderLabel}`} size="small" />
                  </Stack>
                  {Object.entries(statusMap).length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      {Object.entries(statusMap).map(([key, status]) => (
                        <Tooltip
                          key={key}
                          title={status.ok ? 'Operacional' : status.message || 'Erro ao validar'}
                          arrow
                        >
                          <Chip
                            label={`${getProviderLabel(key)} • ${status.ok ? 'ok' : 'erro'}`}
                            color={status.ok ? 'success' : 'error'}
                            size="small"
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Usuários não logados utilizam sempre {guestProviderLabel}. Empresas novas herdam {defaultProviderLabel}.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6">Provedor por empresa</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Defina qual provedor cada empresa autenticada utilizará. Selecionar “Padrão” aplica {defaultProviderLabel}.
              </Typography>
              {companies.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma empresa cadastrada ainda. Novos cadastros herdam {defaultProviderLabel}.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {companies.map((company) => {
                    const value = company.provider || 'default'
                    return (
                      <Paper key={company.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', md: 'center' }}
                        >
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{company.name}</Typography>
                            {company.email && (
                              <Typography variant="body2" color="text.secondary">{company.email}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              Última atualização: {formatDateTime(company.updatedAt || company.llm_provider_updated_at)}
                            </Typography>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <Select
                              value={value}
                              onChange={(event) => handleCompanyProviderChange(company.id, event.target.value)}
                              disabled={updatingCompanyId === company.id}
                            >
                              <MenuItem value="default">{`Padrão (${defaultProviderLabel})`}</MenuItem>
                              {providerOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {getProviderLabel(option)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      </Paper>
                    )
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Liquidez Corrente */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                💧 {liquidezConfig.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                {liquidezConfig.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="error.main" gutterBottom>
                  🔴 Crítico (menor que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={liquidezConfig.critico}
                  onChange={(e) => handleChange('liquidez', 'critico', e.target.value)}
                  inputProps={{ step: 0.1, min: 0 }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="warning.main" gutterBottom>
                  🟡 Atenção (menor que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={liquidezConfig.atencao}
                  onChange={(e) => handleChange('liquidez', 'atencao', e.target.value)}
                  inputProps={{ step: 0.1, min: 0 }}
                  size="small"
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                🟢 Positivo: Maior ou igual a {liquidezConfig.atencao}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Margem Líquida */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                {margemConfig.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                {margemConfig.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="error.main" gutterBottom>
                  🔴 Crítico (menor que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={margemConfig.critico}
                  onChange={(e) => handleChange('margemLiquida', 'critico', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1 }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="warning.main" gutterBottom>
                  🟡 Atenção (menor que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={margemConfig.atencao}
                  onChange={(e) => handleChange('margemLiquida', 'atencao', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1 }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="success.main" gutterBottom>
                  🟢 Positivo (maior ou igual a)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={margemConfig.positivo}
                  onChange={(e) => handleChange('margemLiquida', 'positivo', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1 }}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Endividamento */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                {endividamentoConfig.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                {endividamentoConfig.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="error.main" gutterBottom>
                  🔴 Crítico (maior que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={endividamentoConfig.critico}
                  onChange={(e) => handleChange('endividamento', 'critico', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1, min: 0, max: 100 }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="warning.main" gutterBottom>
                  🟡 Atenção (maior que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={endividamentoConfig.atencao}
                  onChange={(e) => handleChange('endividamento', 'atencao', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1, min: 0, max: 100 }}
                  size="small"
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                🟢 Positivo: Menor ou igual a {endividamentoConfig.atencao}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Crescimento */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                📈 {crescimentoConfig.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
                {crescimentoConfig.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="error.main" gutterBottom>
                  🔴 Crítico (menor que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={crescimentoConfig.critico}
                  onChange={(e) => handleChange('crescimento', 'critico', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1 }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="warning.main" gutterBottom>
                  🟡 Atenção (menor que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={crescimentoConfig.atencao}
                  onChange={(e) => handleChange('crescimento', 'atencao', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1 }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="success.main" gutterBottom>
                  🟢 Positivo (maior que)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={crescimentoConfig.positivo}
                  onChange={(e) => handleChange('crescimento', 'positivo', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ step: 1 }}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
          onClick={() => setConfirmarRestauracao(true)}
          disabled={saving}
          size="large"
        >
          {saving ? 'Aguarde…' : 'Restaurar Padrão'}
        </Button>
        <Dialog open={confirmarRestauracao} onClose={() => setConfirmarRestauracao(false)}>
          <DialogTitle>Restaurar as configurações padrão?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Os limites e pesos que você ajustou aqui voltam aos valores de fábrica, para
              todos os usuários. Não há como desfazer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmarRestauracao(false)}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={handleReset}>Restaurar</Button>
          </DialogActions>
        </Dialog>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
          sx={{
            minWidth: 160,
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          }}
        >
          {saving ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      </Box>
    </Box>
  );
};

export default AlertConfigTab;
